import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/common/Card";
import { Button } from "@/components/common/Button";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { batchesService } from "@/services/batches";
import { OpportunityBatch } from "@/types";
import { errorMessage } from "@/utils/errorMessage";
import { format } from "date-fns";
import {
  FileText,
  Calendar,
  DollarSign,
  AlignLeft,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PAGE_SIZE = 15;

// Search UI is disabled for now (see SHOW_SEARCH below) since batches load one
// page at a time - re-enabling it here would only filter the current page's
// batches, not the whole list. Kept in place in case we want to bring it back
// (e.g. backed by a server-side search) later.
const SHOW_SEARCH = false;

export function BatchesPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["batches", page],
    queryFn: () => batchesService.getBatches(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const batches = data?.data || [];
  const pagination = data?.pagination;

  const filteredBatches = SHOW_SEARCH
    ? batches.filter((batch: OpportunityBatch) =>
        batch.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : batches;

  const handleCreateJournal = (batchName: string) => {
    navigate(`/journal/${encodeURIComponent(batchName)}`);
  };

  const handleViewJournal = (batchName: string) => {
    navigate(`/journal/snapshot/${encodeURIComponent(batchName)}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center text-red-600 dark:text-red-400">
          <p>Error loading batches: {errorMessage(error, "Failed to load batches")}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Reconciliation Batches
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage Salesforce opportunity batches for journal posting
          </p>
        </div>
      </div>

      {/* Search */}
      {SHOW_SEARCH && (
        <Card>
          <input
            type="text"
            placeholder="Search batches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          />
        </Card>
      )}

      {/* Batches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBatches.map((batch: OpportunityBatch) => {
          const isPosted = !!batch.postedToSageDateTime;
          const displayStatus = isPosted ? "Posted" : batch.status;

          return (
            <Card key={batch.name} className="hover:shadow-lg transition-shadow">
              <div className="space-y-4 flex flex-col flex-grow">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {batch.name}
                  </h3>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        isPosted
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                      }`}
                    >
                      {displayStatus}
                    </span>
                    <span
                      className={`text-xs ${isPosted && batch.postedToSageDateTime ? "text-gray-500 dark:text-gray-400" : "invisible"}`}
                    >
                      {isPosted && batch.postedToSageDateTime
                        ? format(new Date(batch.postedToSageDateTime), "MMM d, yyyy")
                        : "\u00A0"}
                    </span>
                  </div>
                </div>

                {batch.description && (
                  <div className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                    <AlignLeft className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{batch.description}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <DollarSign className="h-4 w-4 mr-2" />
                    <span className="font-medium">${batch.totalAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <FileText className="h-4 w-4 mr-2" />
                    <span>{batch.totalOpportunities} opportunities</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{format(new Date(batch.createdDate), "MMM d, yyyy")}</span>
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  {isPosted ? (
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => handleViewJournal(batch.name)}
                      title={
                        batch.hasPostingSnapshot
                          ? "View the journal entries that were posted"
                          : "No snapshot available — this batch was posted before snapshot recording was enabled. You can still unlock it from here."
                      }
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      View Journal
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => handleCreateJournal(batch.name)}
                    >
                      Create Journal
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {filteredBatches.length === 0 && (
        <Card>
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <p>No batches found</p>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} batches)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
